import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Tent, Car, ArrowRight, Calendar, MapPin, CheckCircle2, Megaphone, Plus, Clock, XCircle,
} from "lucide-react";
import { api, peso } from "../../lib/api";
import { BrutalButton, BrutalCard, Badge, SectionTitle } from "../../components/common/Brutal";
import { useAuth } from "../../contexts/AuthContext";

export default function CamperHome() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const [b, a, act] = await Promise.all([
      api.get("/bookings/me"), api.get("/announcements"), api.get("/activities"),
    ]);
    // Merge any retrieved bookings from localStorage
    const retrieved = JSON.parse(localStorage.getItem("kf_retrieved_bookings") || "[]");
    const ids = new Set(b.data.map((x) => x.id));
    const merged = [...b.data, ...retrieved.filter((r) => !ids.has(r.id))];
    setBookings(merged);
    setAnnouncements(a.data);
    setActivities(act.data);
  };

  useEffect(() => { load().finally(() => setLoading(false)); }, []);

  const STATUS_BADGE = {
    confirmed: { color: "green", icon: CheckCircle2, label: "CONFIRMED" },
    unconfirmed: { color: "amber", icon: Clock, label: "UNCONFIRMED" },
    cancelled: { color: "red", icon: XCircle, label: "CANCELLED" },
  };

  return (
    <div className="space-y-8">
      <section className="kf-border bg-[#E5247C] text-white p-6 sm:p-8 kf-shadow relative overflow-hidden" data-testid="camper-hero">
        <div className="absolute -right-10 -top-10 h-40 w-40 bg-[#FFB400] kf-border rotate-12" />
        <div className="relative">
          <div className="text-xs font-bold tracking-[0.3em] uppercase">Welcome,</div>
          <h1 className="display text-4xl sm:text-5xl mt-1">{user?.firstname || user?.name}</h1>
          <p className="mt-3 max-w-md text-white/90">Your camping pass, schedule and announcements all live here.</p>
        </div>
      </section>

      <section>
        <div className="flex items-center justify-between mb-3">
          <SectionTitle kicker="Your Bookings" title="Camp Pass" />
          <Link to="/booking" data-testid="new-booking-link">
            <BrutalButton variant="primary" className="px-3 py-2 text-xs"><Plus className="h-3 w-3" /> New Booking</BrutalButton>
          </Link>
        </div>

        {loading && <div>Loading…</div>}
        {!loading && bookings.length === 0 && (
          <BrutalCard className="p-8 text-center" data-testid="no-bookings">
            <Tent className="h-10 w-10 mx-auto text-[#E5247C]" strokeWidth={2.5} />
            <h3 className="display text-2xl mt-3">No bookings yet</h3>
            <p className="text-[#6B4E9E] mt-1">Book your camping slot to unlock your QR pass (visible after admin confirms payment).</p>
            <Link to="/booking" className="inline-block mt-4">
              <BrutalButton variant="primary">Book Now <ArrowRight className="h-4 w-4" /></BrutalButton>
            </Link>
          </BrutalCard>
        )}

        <div className="space-y-4">
          {bookings.map((b) => {
            const s = STATUS_BADGE[b.payment_status] || STATUS_BADGE.unconfirmed;
            return (
              <BrutalCard key={b.id} className="p-5" data-testid={`booking-${b.id}`}>
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      {b.camping_type === "tent" ? <Tent className="h-5 w-5" strokeWidth={2.75} /> : <Car className="h-5 w-5" strokeWidth={2.75} />}
                      <h3 className="display text-2xl">{b.zone_name}</h3>
                    </div>
                    <div className="mt-1 text-xs uppercase tracking-widest text-[#6B4E9E]">Booking ID: <b>{b.booking_id || b.id?.slice(0,8)}</b></div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      <Badge color={s.color}>{s.label}</Badge>
                      <Badge color="yellow">{b.persons.length} persons</Badge>
                      <Badge color="magenta">{b.camping_type}</Badge>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs font-bold tracking-widest uppercase text-[#6B4E9E]">Total</div>
                    <div className="display text-3xl">{peso(b.total_amount)}</div>
                  </div>
                </div>
                <div className="mt-3 grid sm:grid-cols-3 gap-3 text-sm">
                  <Stat icon={Calendar} label="Check-in" value={b.check_in} />
                  <Stat icon={Calendar} label="Check-out" value={b.check_out} />
                  <Stat icon={MapPin} label="Nights" value={b.nights} />
                </div>
                {b.payment_status === "cancelled" && b.cancellation_reason && (
                  <div className="mt-3 kf-border bg-[#FFF4E6] p-3 text-xs">
                    <b>Cancellation reason:</b> {b.cancellation_reason}
                    {b.cancelled_at && <div className="text-[#6B4E9E] mt-1">on {new Date(b.cancelled_at).toLocaleString()}</div>}
                  </div>
                )}
                <div className="mt-4 flex flex-wrap gap-2">
                  {b.payment_status === "unconfirmed" && !b.payment_received_at && (
                    <Link to={`/payment/${b.id}`}>
                      <BrutalButton variant="primary" className="px-3 py-2 text-xs">Submit Payment <ArrowRight className="h-3 w-3" /></BrutalButton>
                    </Link>
                  )}
                  {b.payment_status === "unconfirmed" && b.payment_received_at && (
                    <Badge color="amber">Awaiting admin confirmation</Badge>
                  )}
                  {b.payment_status === "confirmed" && (
                    <Link to="/camper/qr">
                      <BrutalButton variant="secondary" className="px-3 py-2 text-xs">View QR Pass <ArrowRight className="h-3 w-3" /></BrutalButton>
                    </Link>
                  )}
                </div>
              </BrutalCard>
            );
          })}
        </div>
      </section>

      <section className="grid md:grid-cols-2 gap-6">
        <BrutalCard className="p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="display text-2xl flex items-center gap-2"><Megaphone className="h-5 w-5 text-[#E5247C]" />Latest News</h3>
            <Link to="/camper/announcements" className="text-xs font-bold uppercase tracking-widest text-[#E5247C] hover:underline">All →</Link>
          </div>
          <div className="space-y-3">
            {announcements.slice(0, 3).map((a) => (
              <div key={a.id} className="kf-border bg-[#FFF4E6] p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Badge color={a.priority === "urgent" ? "red" : a.priority === "warning" ? "amber" : "yellow"}>{a.priority}</Badge>
                  <h4 className="font-bold">{a.title}</h4>
                </div>
                <p className="text-sm text-[#6B4E9E]">{a.message}</p>
              </div>
            ))}
            {announcements.length === 0 && <p className="text-sm text-[#6B4E9E]">No announcements yet.</p>}
          </div>
        </BrutalCard>

        <BrutalCard className="p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="display text-2xl flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-[#E5247C]" />Upcoming</h3>
            <Link to="/camper/schedule" className="text-xs font-bold uppercase tracking-widest text-[#E5247C] hover:underline">All →</Link>
          </div>
          <div className="space-y-3">
            {activities.slice(0, 3).map((a) => (
              <div key={a.id} className="kf-border bg-[#FFF4E6] p-3">
                <div className="text-xs font-bold uppercase tracking-widest text-[#E5247C]">{a.date} · {a.start_time}</div>
                <div className="font-bold mt-1">{a.name}</div>
                <div className="text-xs text-[#6B4E9E] mt-0.5">{a.location}</div>
              </div>
            ))}
            {activities.length === 0 && <p className="text-sm text-[#6B4E9E]">No activities yet.</p>}
          </div>
        </BrutalCard>
      </section>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value }) => (
  <div className="kf-border bg-[#FFF4E6] p-3 flex items-center gap-2">
    <Icon className="h-4 w-4 text-[#E5247C]" strokeWidth={2.75} />
    <div>
      <div className="text-[10px] font-bold uppercase tracking-widest text-[#6B4E9E]">{label}</div>
      <div className="font-bold text-sm">{value}</div>
    </div>
  </div>
);
